import { useState, ChangeEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
} from '@mui/material';
import {
  PegoutQuote,
  RegisterPeginParams,
  Mempool,
} from '@rsksmart/flyover-sdk';
import flyover from '../flyover';
import useStore from '../hooks/useStore';
import FlyAlert from './UI/FlyAlert';

interface RefundModalProps {
  open: boolean;
  onClose: () => void;
}

type ParsedJson = { type: 'pegin'; json: RegisterPeginParams } | { type: 'pegout'; json: PegoutQuote };

function RefundModal({ open, onClose }: RefundModalProps) {
  const [parsed, setParsed] = useState<ParsedJson | null>(null);
  const [btcTxHash, setBtcTxHash] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [successTxHash, setSuccessTxHash] = useState('');

  const {
    flyover: { lpList },
  } = useStore();

  function resetState() {
    setParsed(null);
    setBtcTxHash('');
    setError('');
    setSuccessTxHash('');
  }

  function handleClose() {
    if (isProcessing) return;
    resetState();
    onClose();
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result as string);        
        if ('providerSignature' in json) {
          if (!json.quote || !json.quote.quote) {
            throw new Error('Invalid pegin format');
          }
          const data = json;
          setParsed({ type: 'pegin', json });
          if (data.userBtcTransactionHash) setBtcTxHash(data.userBtcTransactionHash);
        } 
        else if ('quote' in json && 'quoteHash' in json) {
          if (!json.quote.liquidityProviderRskAddress) {
            throw new Error('Invalid pegout format');
          }
          setParsed({ type: 'pegout', json });
        } 
        else {
          throw new Error('Invalid format');
        }
        setError('');
      } catch (err) {
        setError('Malformed file data, please check your uploaded file');
      }
    };
    reader.readAsText(file);
  }

  async function handleRefund() {
    if (!parsed) return;
    const mempool = new Mempool((process.env.REACT_APP_ENV?.toLowerCase() ?? "testnet") as "testnet" | "mainnet");
    flyover.connectToBitcoin(mempool);
    setIsProcessing(true);
    setError('');
    
    try {
      if (parsed.type === 'pegin') {
        const { quote, providerSignature } = parsed.json;
        const providerAddr = quote.quote.lpRSKAddr.toLowerCase();
        console.log('provider address: ', providerAddr);

        const provider = lpList.find(
          (lp) => lp.provider.toLowerCase() === providerAddr,
        ) ?? lpList[0];
        flyover.useLiquidityProvider(provider);
        console.log('Provider set')

        const btcHash = btcTxHash.trim();
        if (!btcHash) throw new Error('BTC transaction hash is required');

        const isPaid = (await flyover.isQuotePaid(quote.quoteHash, 'pegin'));
        console.log('isQuotePaidResult: ', isPaid);
        if (isPaid.isPaid) throw new Error('Quote has been already paid by the LPS');
        
        const isRefundable = await flyover.isPeginRefundable({quote, providerSignature, btcTransactionHash: btcHash});
        console.log('isPeginRefundableResult: ', isRefundable);
        if (!isRefundable.isRefundable) throw new Error(isRefundable.error?.detail?.message ?? 'Quote is not refundable');

        const params: RegisterPeginParams = {
          quote,
          providerSignature,
          userBtcTransactionHash: btcHash,
        };
        console.log('registerPeginParams: ', params);
        
        const tx = await flyover.registerPegin(params);
        setSuccessTxHash(tx);
      } else {
        const providerAddr = parsed.json.quote.liquidityProviderRskAddress.toLowerCase()
        console.log('provider address: ', providerAddr);

        const provider = lpList.find((lp) => lp.provider.toLowerCase() === providerAddr) ?? lpList[0];
        flyover.useLiquidityProvider(provider);
        console.log('Provider set')

        const isPaid = await flyover.isQuotePaid(parsed.json.quoteHash, 'pegout')
        console.log('isQuotePaidResult: ', isPaid);
        if (isPaid.isPaid) throw new Error('Quote has been already paid by the LPS');

        const isRefundable = await flyover.isPegoutRefundable(parsed.json);
        console.log('isPegoutRefundableResult: ', isRefundable);
        if (!isRefundable.isRefundable) throw new Error(isRefundable.error?.detail?.message ?? 'Quote is not refundable');

        const tx = await flyover.refundPegout(parsed.json);
        setSuccessTxHash(tx);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refund failed');
    } finally {
      setIsProcessing(false);
    }
  }

  const showBtcInput = parsed?.type === 'pegin' && !parsed?.json.userBtcTransactionHash;
  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="sm" 
      disableRestoreFocus
      PaperProps={{
        sx: { borderRadius: 3 },
      }}>
      <DialogTitle>Perform a refund</DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Stack spacing={2}>
          <Button
            variant="outlined"
            component="label"
            disabled={isProcessing}
            sx={{ borderRadius: 2 }}
          >
            Upload JSON
            <input
              type="file"
              hidden
              accept="application/json"
              onChange={handleFileChange}
            />
          </Button>
          {showBtcInput && (
            <TextField
              label="BTC transaction hash"
              value={btcTxHash}
              onChange={(e) => setBtcTxHash(e.target.value)}
              variant="standard"
              fullWidth
            />
          )}
          {error && <FlyAlert severity="error">{error}</FlyAlert>}
          {successTxHash && (
            <FlyAlert severity="success">
              Success! Tx hash: {successTxHash}
            </FlyAlert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Close
        </Button>
        <Button
          onClick={handleRefund}
          disabled={!parsed || (showBtcInput && btcTxHash.trim() === '') || isProcessing}
        >
          {isProcessing ? 'Processing…' : 'Refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RefundModal;
